import type { SavedEstimate } from '@/hooks/useEstimates'
import { useAuth } from '@/hooks/useAuth'
import { useEstimates } from '@/hooks/useEstimates'
import {Check, Droplet, Loader2, Pencil, Syringe, Trash2, X} from 'lucide-react'
import { useEffect, useState } from 'react'

interface SavedEstimatesDrawerProps {
  open: boolean
  onClose: () => void
}

function EstimateCard({
  estimate,
  onDelete,
  onRename,
}: {
  estimate: SavedEstimate
  onDelete: (id: string) => void
  onRename: (id: string, label: string) => Promise<void>
}) {
  const isLaser = estimate.calculatorType === 'laser'
  const fallbackName = isLaser ? 'Laser Removal Estimate' : 'Tattoo Cost Estimate'
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(estimate.label || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const startEdit = () => {
    setDraft(estimate.label || '')
    setError('')
    setEditing(true)
  }

  const cancelEdit = () => {
    setDraft(estimate.label || '')
    setError('')
    setEditing(false)
  }

  const saveLabel = async () => {
    const next = draft.trim()
    if (next === (estimate.label || '').trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError('')
    try {
      await onRename(estimate._id, next)
      setEditing(false)
    }
    catch {
      setError('Could not save this label. Please try again.')
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          {isLaser
            ? <Syringe className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" aria-hidden="true" />
            : <Droplet className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" aria-hidden="true" />}
          <div className="min-w-0 flex-1">
            {editing
              ? (
                  <form
                    className="space-y-2"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void saveLabel()
                    }}
                  >
                    <label htmlFor={`estimate-label-${estimate._id}`} className="sr-only">
                      Custom estimate label
                    </label>
                    <input
                      id={`estimate-label-${estimate._id}`}
                      type="text"
                      value={draft}
                      maxLength={80}
                      autoFocus
                      disabled={saving}
                      placeholder={fallbackName}
                      onChange={event => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault()
                          cancelEdit()
                        }
                      }}
                      className="w-full rounded-lg border border-amber-500/40 bg-black/40 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 disabled:opacity-60"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-60"
                      >
                        {saving
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                        Save label
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={cancelEdit}
                        className="inline-flex min-h-10 items-center rounded-full px-3 py-1.5 text-xs font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                    {error
                      ? <p className="text-xs text-red-400">{error}</p>
                      : <p className="text-xs text-slate-500">Leave blank to use the default name.</p>}
                  </form>
                )
              : (
                  <>
                    <p className="truncate text-sm font-semibold text-white">
                      {estimate.label || fallbackName}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {new Date(estimate.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </>
                )}
          </div>
        </div>
        {editing
          ? null
          : (
              <div className="flex flex-shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  aria-label={estimate.label ? 'Rename estimate' : 'Add a custom label'}
                  onClick={startEdit}
                  className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-amber-500/10 hover:text-amber-300"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Delete estimate"
                  onClick={() => onDelete(estimate._id)}
                  className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
      </div>
      <p className="mt-3 text-sm text-amber-300">{estimate.summary}</p>
    </div>
  )
}

function SavedEstimatesDrawer({ open, onClose }: SavedEstimatesDrawerProps) {
  const { user, isAuthenticated } = useAuth()
  const { estimates, loading, fetchEstimates, deleteEstimate, renameEstimate } = useEstimates()

  useEffect(() => {
    if (open && isAuthenticated && user?.userId) {
      fetchEstimates(user.userId)
    }
  }, [open, isAuthenticated, user?.userId, fetchEstimates])

  if (!open)
    return null

  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/70 backdrop-blur-sm">
      <div className="h-full w-full max-w-md overflow-y-auto border-l border-amber-500/20 bg-[#0e0c0a] p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold text-white">My Estimates</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-amber-400"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-400">
          Every tattoo cost and laser removal estimate you've saved to
          {' '}
          <span className="text-amber-300">TattooCostCalculator.com</span>
          {' '}
          is stored here for later reference. Tap the pencil to rename any quote.
        </p>

        <div className="mt-6 space-y-3">
          {loading
            ? (
                <div className="flex items-center justify-center gap-2 py-10 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  Loading...
                </div>
              )
            : null}

          {!loading && estimates.length === 0
            ? (
                <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
                  No saved estimates yet. Use the "Save Estimate" button on either calculator to keep a record here.
                </div>
              )
            : null}

          {!loading
            ? estimates.map(estimate => (
                <EstimateCard
                  key={estimate._id}
                  estimate={estimate}
                  onDelete={deleteEstimate}
                  onRename={renameEstimate}
                />
              ))
            : null}
        </div>
      </div>
    </div>
  )
}

export default SavedEstimatesDrawer
